
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, Eye, ShoppingCart } from 'lucide-react';
import { Product } from '@/types/product';
import { useFavorites } from '@/hooks/useFavorites';
import { useCart } from '@/contexts/CartContext';
import ProductBuyButton from './ProductBuyButton';
import { toast } from 'sonner';
import { imageUrl } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateProductName } from '@/utils/contentTranslation';
import { useNavigate } from 'react-router-dom';

interface ProductCardProps {
  product: Product;
  onQuickView?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onQuickView }) => {
  const { t, currentLanguage } = useLanguage();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    toggleFavorite(product.id);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    addToCart(product, 1);
    // Toast is now handled in CartContext
  };

  const handleNavigateToProduct = () => {
    // Use QuickView modal for both mobile and desktop
    if (onQuickView) {
      onQuickView();
    }
  };

  return (
    <Card className="group relative overflow-hidden bg-white md:hover:shadow-lg transition-all duration-300 border-0 h-[300px] md:h-[500px] flex flex-col">
      {/* Favorite button */}
      <button
        onClick={handleFavoriteToggle}
        className="absolute top-2 md:top-3 right-2 md:right-3 z-10 p-1.5 md:p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-md"
      >
        <Heart
          size={14}
          className={`md:w-4 md:h-4 ${isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'} transition-colors`}
        />
      </button>

      {/* Product image - Clickable */}
      <div
        onClick={handleNavigateToProduct}
        className="relative overflow-hidden bg-gray-50 h-[140px] md:h-[280px] flex-shrink-0 cursor-pointer active:opacity-90 transition-opacity"
      >
        <img
          src={imageUrl(product.image)}
          alt={product.name}
          className="w-full h-full object-cover p-2 md:p-4 md:group-hover:scale-105 md:transition-transform md:duration-300"
          onError={(e) => {
            const target = e.currentTarget as HTMLImageElement;
            // Prevent infinite loop by checking if we're already showing the fallback
            if (!target.src.includes('60244da3-6deb-4628-b29a-c8e90866808e.png')) {
              target.src = imageUrl('/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png');
            } else {
              // If even the fallback fails, show a simple placeholder
              target.style.display = 'none';
              target.parentElement?.classList.add('bg-gray-200');
            }
          }}
        />

        {/* Overlay buttons - Hidden on mobile */}
        <div className="hidden md:flex absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 items-center justify-center gap-2">
          {onQuickView && (
            <Button
              onClick={onQuickView}
              size="sm"
              variant="secondary"
              className="bg-white/90 text-gray-900 hover:bg-white"
            >
              <Eye size={16} className="mr-1" />
              {t('products.view')}
            </Button>
          )}
          <Button
            onClick={handleAddToCart}
            size="sm"
            className="bg-brand-red hover:bg-brand-red/90"
          >
            <ShoppingCart size={16} className="mr-1" />
            {t('products.addToCartShort')}
          </Button>
        </div>
      </div>

      <CardContent className="p-2 md:p-4 flex flex-col flex-grow">
        {/* Product name - Fixed height container - Clickable */}
        <div
          onClick={handleNavigateToProduct}
          className="h-[32px] md:h-[72px] mb-1.5 md:mb-2 flex items-center justify-center cursor-pointer hover:text-brand-red transition-colors"
        >
          <h3 className="font-medium text-gray-900 line-clamp-2 md:line-clamp-3 text-xs md:text-base leading-tight text-center">
            {translateProductName(product.name, currentLanguage)}
          </h3>
        </div>

        {/* Bottom section - Price and Buttons */}
        <div className="space-y-1.5 md:space-y-3 mt-auto">
          {/* Points price */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-2">
            <span className="text-sm md:text-lg font-bold text-brand-red">
              {product.pointsPrice}
            </span>
            <span className="text-xs md:text-base font-medium text-brand-red">
              {t('products.points')}
            </span>
            {product.price && product.price > 0 && (
              <span className="text-xs md:text-sm text-gray-500 line-through">
                €{product.price.toFixed(2)}
              </span>
            )}
          </div>

          {/* Mobile: Icon Buttons Side by Side | Desktop: Single Exchange Button */}
          <div className="md:hidden flex gap-2">
            {/* Add to Cart Icon Button - Mobile Only */}
            <Button
              onClick={handleAddToCart}
              variant="outline"
              size="sm"
              type="button"
              className="flex-1 border-2 border-brand-red text-brand-red hover:bg-brand-red/10 py-2 h-9 active:scale-95 transition-transform"
              title={t('products.addToCart')}
            >
              <ShoppingCart size={16} />
            </Button>

            {/* Exchange Icon Button - Mobile */}
            <ProductBuyButton
              product={product}
              quantity={1}
              variant="default"
              size="sm"
              iconOnly={true}
              className="flex-1 bg-brand-red hover:bg-brand-red/90 text-white py-2 h-9 active:scale-95 transition-transform"
            />
          </div>

          {/* Desktop: Single Exchange Button */}
          <div className="hidden md:block">
            <ProductBuyButton
              product={product}
              quantity={1}
              className="w-full bg-brand-red hover:bg-brand-red/90 text-sm py-2 h-10"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
